import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import {
  Check,
  ExternalLink,
  Download,
  Twitter,
  Github,
  Linkedin,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Sun,
  Moon,
} from "lucide-react";
import { describeBrand, type BrandProfile } from "@/lib/api";
import { IMG_BASE, STYLE_VARIANTS, MARQUEE_BRANDS } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";
import { useCopyFeedback } from "@/hooks/use-copy-feedback";
import { cn } from "@/lib/utils";

const SOCIAL_ICONS: Record<string, React.ElementType> = {
  twitter: Twitter,
  github: Github,
  linkedin: Linkedin,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

const DOWNLOAD_SIZES = [32, 64, 128, 256, 512] as const;
const DOWNLOAD_FORMATS = ["png", "webp", "svg"] as const;

const checkerStyle: React.CSSProperties = {
  backgroundSize: "20px 20px",
  backgroundPosition: "0 0, 10px 10px",
  backgroundImage: "linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%), linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%)",
};
const checkerDarkStyle: React.CSSProperties = {
  backgroundSize: "20px 20px",
  backgroundPosition: "0 0, 10px 10px",
  backgroundImage: "linear-gradient(45deg, #1f2937 25%, transparent 25%, transparent 75%, #1f2937 75%), linear-gradient(45deg, #1f2937 25%, transparent 25%, transparent 75%, #1f2937 75%)",
};

interface BrandPageProps {
  serverData?: BrandProfile | null;
}

export default function BrandPage({ serverData }: BrandPageProps) {
  const { domain } = useParams<{ domain: string }>();
  const [brand, setBrand] = useState<BrandProfile | null>(serverData ?? null);
  const [loading, setLoading] = useState(!serverData);
  const { copiedValue: copiedColor, copy } = useCopyFeedback<string>(1500);
  const [darkPreview, setDarkPreview] = useState(false);

  useEffect(() => {
    if (serverData) return;
    if (!domain) return;
    setLoading(true);
    setBrand(null);
    describeBrand(domain)
      .then(setBrand)
      .catch(() => setBrand({ error: "Failed to load brand data" } as BrandProfile))
      .finally(() => setLoading(false));
  }, [domain, serverData]);

  const copyColor = (hex: string) => {
    void copy(hex, hex);
  };

  if (loading) {
    return (
      <div className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Loading brand profile...</p>
      </div>
    );
  }

  if (!brand || brand.error) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Brand not found</h1>
          <p className="text-sm text-secondary-foreground mb-6">
            {brand?.error || `We couldn't find brand data for "${domain}".`}
          </p>
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const relatedBrands = MARQUEE_BRANDS.filter((b) => b !== domain).slice(0, 8);

  // Clean up brand name — strip common page title suffixes
  const cleanName = (brand.name || domain || "")
    .replace(/\s*[-–—|·:]\s*.{10,}$/, "") // "Spotify – Web Player: Music for everyone" → "Spotify"
    .replace(/\s*\|.*$/, "")
    .trim() || domain!;

  // Filter out default/bad CSS colors that sneak in from scraping
  const BAD_COLORS = new Set(["#0000ee", "#0000ff", "#ffffff", "#000000", "#333333", "#666666"]);
  const filteredColors = (brand.colors || []).filter(
    (c) => !BAD_COLORS.has(c.hex.toLowerCase())
  );

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Logos", to: "/" },
        { label: cleanName },
      ]}
    >
      {/* Hero */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0 relative">
            <img
              src={`${IMG_BASE}/${domain}?size=256${darkPreview ? "&theme=dark" : ""}`}
              alt={`${cleanName} logo`}
              className={cn(
                "w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-contain border shadow-sm transition-colors",
                darkPreview ? "bg-gray-900 border-gray-700" : "bg-white"
              )}
              style={darkPreview ? checkerDarkStyle : checkerStyle}
              onError={(e) => {
                (e.target as HTMLElement).style.opacity = "0.3";
              }}
            />
            <button
              onClick={() => setDarkPreview((d) => !d)}
              className="absolute -bottom-2 -right-2 w-7 h-7 rounded-lg bg-card border shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title={darkPreview ? "Light background" : "Dark background"}
            >
              {darkPreview ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
              {cleanName}
            </h1>
            {brand.description && (
              <p className="text-base text-secondary-foreground leading-relaxed mb-3 max-w-2xl">
                {brand.description}
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">{domain}</Badge>
              {brand.indexed_at && (
                <Badge variant="outline">
                  Indexed {new Date(brand.indexed_at).toLocaleDateString()}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/${domain}" />`} />
      </section>

      {/* Brand Colors */}
      {filteredColors.length > 0 && (
        <section className="mb-12">
          <h2 className="text-lg font-bold tracking-tight mb-3">Brand Colors</h2>
          <div className="flex gap-3 flex-wrap">
            {filteredColors.map((c) => (
              <button
                key={c.hex}
                onClick={() => copyColor(c.hex)}
                className="group flex flex-col items-center gap-1.5 cursor-pointer"
              >
                <div
                  className="w-14 h-14 rounded-xl border-2 border-card shadow-sm hover:scale-110 transition-transform"
                  style={{
                    background: c.hex,
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
                  }}
                />
                <span className="text-[11px] font-mono text-muted-foreground group-hover:text-foreground transition-colors">
                  {copiedColor === c.hex ? (
                    <span className="flex items-center gap-0.5">
                      <Check className="w-3 h-3" /> Copied
                    </span>
                  ) : (
                    c.hex
                  )}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Social Links */}
      {Object.keys(brand.socials || {}).length > 0 && (
        <section className="mb-12">
          <h2 className="text-lg font-bold tracking-tight mb-3">Social Links</h2>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(brand.socials).map(([platform, url]) => {
              const Icon = SOCIAL_ICONS[platform] || Globe;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline"
                >
                  <Icon className="w-4 h-4" />
                  <span className="capitalize">{platform}</span>
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Logo Sources */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Available Logos</h2>
        <p className="text-sm text-secondary-foreground mb-4">
          Different logo formats available for {cleanName} — use whichever fits your use case.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { label: "Logo", sublabel: "Best quality", url: `${IMG_BASE}/${domain}?size=256${darkPreview ? "&theme=dark" : ""}`, code: `img.loadlogo.com/${domain}` },
            { label: "Favicon", sublabel: "Site icon", url: `${IMG_BASE}/favicon/${domain}?size=256${darkPreview ? "&theme=dark" : ""}`, code: `img.loadlogo.com/favicon/${domain}` },
            { label: "Monochrome", sublabel: "Single color", url: `${IMG_BASE}/${domain}?size=256&style=monochrome${darkPreview ? "&theme=dark" : ""}`, code: `img.loadlogo.com/${domain}?style=monochrome` },
          ].map((src) => (
            <div
              key={src.label}
              className="flex flex-col items-center gap-2 p-4 bg-card border rounded-xl"
            >
              <div
                className={cn(
                  "w-24 h-24 flex items-center justify-center rounded-xl border p-2 transition-colors",
                  darkPreview ? "bg-gray-900 border-gray-700" : "bg-white"
                )}
                style={darkPreview ? checkerDarkStyle : checkerStyle}
              >
                <img
                  src={src.url}
                  alt={`${cleanName} ${src.label}`}
                  loading="lazy"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLElement).style.opacity = "0.15";
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-foreground">{src.label}</span>
              <span className="text-[10px] text-muted-foreground">{src.sublabel}</span>
              <code className="text-[9px] font-mono text-muted-foreground/60 truncate max-w-full">{src.code}</code>
            </div>
          ))}
        </div>
      </section>

      {/* Style Variants */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold tracking-tight">Style Variants</h2>
          <button
            onClick={() => setDarkPreview((d) => !d)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-card border text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            {darkPreview ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            {darkPreview ? "Light" : "Dark"} preview
          </button>
        </div>
        <p className="text-sm text-secondary-foreground mb-4">
          Transform the {cleanName} logo with AI-powered style variants using a single query parameter.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STYLE_VARIANTS.map((s) => {
            const imgUrl = `${IMG_BASE}/${domain}?size=256${s.param ? `&style=${s.param}` : ""}${darkPreview ? "&theme=dark" : ""}`;
            return (
              <div
                key={s.name}
                className="flex flex-col items-center gap-2 p-4 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div
                  className={cn(
                    "w-24 h-24 flex items-center justify-center rounded-xl border p-2 transition-colors",
                    darkPreview ? "bg-gray-900 border-gray-700" : "bg-white"
                  )}
                  style={darkPreview ? checkerDarkStyle : checkerStyle}
                >
                  <img
                    src={imgUrl}
                    alt={`${cleanName} ${s.name} style`}
                    loading="lazy"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.opacity = "0.3";
                    }}
                  />
                </div>
                <span className="text-xs font-semibold text-muted-foreground">{s.name}</span>
                {(s.param || darkPreview) && (
                  <code className="text-[10px] font-mono text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded">
                    {s.param ? `?style=${s.param}` : ""}{s.param && darkPreview ? "&" : !s.param && darkPreview ? "?" : ""}
                    {darkPreview ? "theme=dark" : ""}
                  </code>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Download Options */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Download Options</h2>
        <p className="text-sm text-secondary-foreground mb-4">
          Download the {cleanName} logo in various sizes and formats.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-semibold text-muted-foreground">Size</th>
                {DOWNLOAD_FORMATS.map((fmt) => (
                  <th key={fmt} className="text-left py-2 px-4 font-semibold text-muted-foreground uppercase">
                    {fmt}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DOWNLOAD_SIZES.map((size) => (
                <tr key={size} className="border-b border-border/50">
                  <td className="py-2.5 pr-4 font-mono text-sm">
                    {size}x{size}
                  </td>
                  {DOWNLOAD_FORMATS.map((fmt) => (
                    <td key={fmt} className="py-2.5 px-4">
                      <a
                        href={`${IMG_BASE}/${domain}?size=${size}&format=${fmt}&download=1`}
                        download={`${domain?.replace(/\./g, "-")}-${size}x${size}.${fmt}`}
                        className="inline-flex items-center gap-1 text-primary text-xs font-medium hover:underline"
                      >
                        <Download className="w-3 h-3" />
                        {fmt.toUpperCase()}
                      </a>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* API Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Basic logo -->\n<img src="${IMG_BASE}/${domain}" />\n\n<!-- With size and format -->\n<img src="${IMG_BASE}/${domain}?size=256&format=webp" />\n\n<!-- Flat style variant -->\n<img src="${IMG_BASE}/${domain}?style=flat" />\n\n<!-- Brand profile (JSON) -->\n<script>\nfetch("https://api.loadlogo.com/describe/${domain}")\n</script>`} />
      </section>

      {/* Related Logos */}
      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Related Logos</h2>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          {relatedBrands.map((d) => (
            <Link
              key={d}
              to={`/logo/${d}`}
              className="flex flex-col items-center gap-2 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <img
                src={`${IMG_BASE}/${d}?size=64`}
                alt={d}
                className="w-8 h-8 object-contain"
                loading="lazy"
              />
              <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                {d.replace(/\.(com|io|org|co|tv|us|so)$/, "")}
              </span>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
